import React from 'react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { ButtonV2 } from '@/components/ui/ButtonV2';
import { Popover } from '@/components/ui/Popover';
import { useNotifications } from '@/context/NotificationsContext';
import { formatDistanceToNowStrict } from '@/lib/time';
import { cn } from '@/lib/utils';

export function NotificationsBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = React.useState(false);

  const handleSelect = async (id: string, read: boolean) => {
    if (!read) {
      await markAsRead(id);
    }
    setOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Only show unread notifications
  const unreadNotifications = notifications.filter(n => !n.read);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      asChild
      align="left"
      usePortal
      trigger={
        <ButtonV2
          size="icon-lg"
          variant={open ? 'active' : 'ghost'}
          aria-label="Notifications"
        >
          <div className="relative">
            <BellAlertIcon className={cn(
              "h-5 w-5 transition-colors",
              unreadCount > 0 && !open && "text-primary"
            )} />
            {unreadCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] rounded-full bg-destructive text-[10px] font-semibold text-white flex items-center justify-center px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        </ButtonV2>
      }
    >
      <div className="w-80 max-h-96 flex flex-col">
        <div className="px-3 py-2 flex items-center justify-between border-b border-border/50">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Notifications
          </h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <ButtonV2
                onClick={handleMarkAllAsRead}
                variant="link"
                size="sm"
              >
                Mark all read
              </ButtonV2>
            )}
          </div>
        </div>

        <div className="overflow-y-auto p-2 flex-1">
          {unreadNotifications.length === 0 && (
            <div className="mx-2 my-4 rounded-lg px-3 py-6 text-center text-xs text-muted-foreground">
              No unread notifications.
            </div>
          )}

          {unreadNotifications.map((notification) => (
          <button
            key={notification.id}
            onClick={() => handleSelect(notification.id, notification.read)}
            className={cn(
              'w-full text-left px-3 py-3 rounded-lg transition-colors',
              notification.read
                ? 'hover:bg-muted/50'
                : 'bg-primary/5 hover:bg-primary/10',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={cn(
                'text-sm font-medium line-clamp-1 min-w-0',
                notification.read ? 'text-muted-foreground' : 'text-foreground'
              )}>
                {notification.title}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {formatDistanceToNowStrict(notification.createdAt)}
              </span>
            </div>
            {notification.description && (
              <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                {notification.description}
              </div>
            )}
            {notification.status && (
              <div className="mt-2 inline-flex items-center rounded-full bg-muted/50 px-2 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                {notification.status.replace(/_/g, ' ')}
              </div>
            )}
          </button>
          ))}
        </div>
      </div>
    </Popover>
  );
}

export default NotificationsBell;
